import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { useToast } from '@/hooks/use-toast';
import { Badge } from "@/components/ui/badge";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { format } from 'date-fns';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreHorizontal, Ban, Lock, Unlock, Trash2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

type Profile = {
  id: string;
  username: string;
  email?: string;
  created_at: string;
  level: number;
  review_count: number;
  country_code?: string;
  expert?: boolean;
  followers_count: number;
  following_count: number;
  status: 'active' | 'blocked' | 'banned';
};

const Users = () => {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const checkAdminAndFetchUsers = async () => {
      if (!user) {
        console.log('No user found, redirecting to login');
        navigate('/admin-web/login');
        return;
      }

      try {
        console.log('Checking admin status for user:', user.id);
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (adminError) {
          console.error('Error checking admin status:', adminError);
          throw adminError;
        }

        if (!adminData) {
          console.log('User is not an admin');
          toast({
            title: "Access Denied",
            description: "You don't have admin privileges",
            variant: "destructive"
          });
          navigate('/');
          return;
        }

        console.log('Admin confirmed, fetching users...');
        
        // Get profiles data directly from profiles table
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('*');

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }

        console.log('Fetched profiles:', profiles);
        setUsers(profiles || []);
      } catch (error) {
        console.error('Error:', error);
        toast({
          title: "Error",
          description: "Failed to load users. Please check console for details.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchUsers();
  }, [user, navigate, toast]);

  const handleUserAction = async (action: 'block' | 'unblock' | 'ban' | 'delete', profile: Profile) => {
    try {
      if (action === 'ban' && !banReason.trim()) {
        toast({
          title: "Error",
          description: "Please provide a reason for banning the user",
          variant: "destructive"
        });
        return;
      }

      if (action === 'ban') {
        // Add to banned_users table
        const { error: banError } = await supabase
          .from('banned_users')
          .insert({
            email: profile.email,
            username: profile.username,
            reason: banReason,
            banned_by: user?.id
          });

        if (banError) throw banError;
      }

      // Update user status in profiles
      const newStatus = action === 'block' ? 'blocked' : 
                       action === 'unblock' ? 'active' :
                       action === 'ban' ? 'banned' : 
                       profile.status;

      if (action !== 'delete') {
        const { error: statusError } = await supabase
          .from('profiles')
          .update({ status: newStatus })
          .eq('id', profile.id);

        if (statusError) throw statusError;
      }

      if (action === 'delete') {
        // Instead of directly deleting the auth user, mark them as deleted in profiles
        const { error: deleteError } = await supabase
          .from('profiles')
          .update({ 
            status: 'banned',
            deleted_at: new Date().toISOString()
          })
          .eq('id', profile.id);

        if (deleteError) throw deleteError;
      }

      // Refresh user list
      const { data: updatedProfiles, error: refreshError } = await supabase
        .from('profiles')
        .select('*');

      if (refreshError) throw refreshError;

      setUsers(updatedProfiles as Profile[]);
      setShowDeleteDialog(false);
      setShowBanDialog(false);
      setBanReason('');

      toast({
        title: "Success",
        description: `User ${action === 'unblock' ? 'unblocked' : action + 'ed'} successfully`,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: `Failed to ${action} user. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      console.log('Deleting user:', selectedUser.id);
      // Instead of directly deleting the auth user, mark them as deleted in profiles
      const { error: deleteError } = await supabase
        .from('profiles')
        .update({ 
          status: 'banned',
          deleted_at: new Date().toISOString()
        })
        .eq('id', selectedUser.id);

      if (deleteError) throw deleteError;

      // Refresh user list
      const { data: updatedProfiles, error: refreshError } = await supabase
        .from('profiles')
        .select('*');

      if (refreshError) throw refreshError;

      setUsers(updatedProfiles as Profile[]);
      setShowDeleteDialog(false);

      toast({
        title: "Success",
        description: "User deleted successfully",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to delete user",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="p-4">Loading users...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Users ({users.length})</h1>
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Registration Date</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Reviews</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Social</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.username || 'Anonymous'}</TableCell>
                <TableCell>{user.email || 'N/A'}</TableCell>
                <TableCell>
                  <Badge 
                    variant={
                      user.status === 'active' ? "default" : 
                      user.status === 'blocked' ? "secondary" : 
                      "destructive"
                    }
                  >
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {format(new Date(user.created_at), 'MMM d, yyyy HH:mm')}
                </TableCell>
                <TableCell>{user.level}</TableCell>
                <TableCell>{user.review_count}</TableCell>
                <TableCell>{user.country_code || 'N/A'}</TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm">
                    <span>{user.followers_count} followers</span>
                    <span>{user.following_count} following</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={user.expert ? "default" : "secondary"}>
                    {user.expert ? 'Expert' : 'Regular'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {user.status === 'active' ? (
                        <DropdownMenuItem 
                          onClick={() => handleUserAction('block', user)}
                          className="text-yellow-600"
                        >
                          <Lock className="mr-2 h-4 w-4" />
                          Block User
                        </DropdownMenuItem>
                      ) : user.status === 'blocked' && (
                        <DropdownMenuItem 
                          onClick={() => handleUserAction('unblock', user)}
                          className="text-green-600"
                        >
                          <Unlock className="mr-2 h-4 w-4" />
                          Unblock User
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => {
                          setSelectedUser(user);
                          setShowBanDialog(true);
                        }}
                        className="text-red-600"
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        Ban User
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Account
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user account
              and remove all their data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedUser && handleUserAction('delete', selectedUser)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ban User</AlertDialogTitle>
            <AlertDialogDescription>
              This will ban the user and prevent them from accessing their account.
              Please provide a reason for the ban.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              placeholder="Reason for ban"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedUser && handleUserAction('ban', selectedUser)}
              className="bg-red-600 hover:bg-red-700"
            >
              Ban User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
